import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useMode } from "@/context/ModeContext";
import Sidebar from "@/components/Sidebar";
import { MobileHeader } from "@/components/MobileHeader";
import EarningsChart from "@/components/EarningsChart";
import { useReportsData, groupTransactionsByPatient, getDateRange, buildTransactions, calculateClinicPayables, filterFinancialRecords } from "@/hooks/features/useReportsData";
import { db } from "@/db";
import { formatRupees, toRupees } from "@/utils/currency";
import { FileDown, ChevronLeft, ChevronRight, Filter } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { getFinancialScope } from "@/utils/accessPolicy";

export default function Reports({ onLogout }) {
    const doctorId = localStorage.getItem('user_id');
    const { activeMode } = useMode();
    const { memberships, isReceptionistOnly } = useUserRole();

    const financialScope = useMemo(
        () => getFinancialScope(activeMode, memberships, isReceptionistOnly),
        [activeMode, memberships, isReceptionistOnly]
    );

    // Filter state
    const [dateRange, setDateRange] = useState('current_month');
    const [patientId, setPatientId] = useState('');
    const [showFilters, setShowFilters] = useState(true);

    // Pagination state
    const [page, setPage] = useState(0);
    const pageSize = 20;
    const [isExporting, setIsExporting] = useState(false);

    // Fetch data
    const {
        chartData,
        transactions,
        totalCount,
        patients,
        isLoading
    } = useReportsData(doctorId, activeMode, {
        dateRange,
        patientId: patientId || undefined,
        page,
        pageSize,
        financialScope
    });

    const totalPages = Math.ceil(totalCount / pageSize);

    // Export to PDF
    const handleExportPDF = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            // Dynamically import jsPDF to avoid bundle bloat
            const { default: jsPDF } = await import('jspdf');
            const { default: autoTable } = await import('jspdf-autotable');

            const doc = new jsPDF();
            const dateRangeLabel = getDateRangeLabel(dateRange);

            // Title
            doc.setFontSize(18);
            doc.text('Earnings Report', 14, 22);
            doc.setFontSize(11);
            doc.text(`Period: ${dateRangeLabel}`, 14, 30);
            doc.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, 14, 36);

            // Fetch all transactions for export (not just current page)
            const allTransactions = await fetchAllTransactionsForExport();
            const groupedData = groupTransactionsByPatient(allTransactions);

            let yPosition = 46;

            // Add patient-wise sections
            for (const group of groupedData) {
                // Check if we need a new page
                if (yPosition > 250) {
                    doc.addPage();
                    yPosition = 20;
                }

                // Patient header
                doc.setFontSize(12);
                doc.setFont(undefined, 'bold');
                doc.text(`Patient: ${group.patientName}`, 14, yPosition);
                yPosition += 6;

                // Patient transactions table
                const tableData = group.transactions.map(tx => [
                    new Date(tx.date).toLocaleDateString('en-IN'),
                    tx.appointmentEarnings > 0 ? `Rs. ${toRupees(tx.appointmentEarnings).toFixed(2)}` : '-',
                    tx.facilityChargeEarned > 0 ? `Rs. ${toRupees(tx.facilityChargeEarned).toFixed(2)} (from ${tx.facilityChargeEarnedFromDoctor || 'N/A'})` : '-',
                    tx.facilityChargePayable > 0 ? `Rs. ${toRupees(tx.facilityChargePayable).toFixed(2)} (to ${tx.facilityChargePayableToDoctor || 'N/A'})` : '-',
                    `Rs. ${toRupees(tx.totalEarning).toFixed(2)}`
                ]);

                // Add totals row
                tableData.push([
                    'TOTAL',
                    `Rs. ${toRupees(group.totals.appointmentEarnings).toFixed(2)}`,
                    `Rs. ${toRupees(group.totals.facilityChargeEarned).toFixed(2)}`,
                    `Rs. ${toRupees(group.totals.facilityChargePayable).toFixed(2)}`,
                    `Rs. ${toRupees(group.totals.totalEarning).toFixed(2)}`
                ]);

                autoTable(doc, {
                    startY: yPosition,
                    head: [['Date', 'Appointment', 'Facility Charge Earned', 'Facility Charge Payable', 'Total']],
                    body: tableData,
                    theme: 'striped',
                    headStyles: { fillColor: [41, 128, 185], fontSize: 9 },
                    bodyStyles: { fontSize: 8 },
                    columnStyles: {
                        0: { cellWidth: 25 },
                        1: { cellWidth: 30 },
                        2: { cellWidth: 50 },
                        3: { cellWidth: 50 },
                        4: { cellWidth: 25 }
                    },
                    didDrawPage: (data) => {
                        yPosition = data.cursor.y + 10;
                    }
                });

                yPosition = doc.lastAutoTable.finalY + 15;
            }

            // --- Summary 1: Patient-wise total appointment charge ---
            if (groupedData.length > 0) {
                if (yPosition > 250) {
                    doc.addPage();
                    yPosition = 20;
                } else if (yPosition !== 46) {
                    yPosition += 10;
                }

                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                doc.text('Summary: Patient-wise Appointment Charges', 14, yPosition);
                yPosition += 6;

                const patientSummaryData = groupedData.map(group => [
                    group.patientName,
                    `Rs. ${toRupees(group.totals.appointmentEarnings).toFixed(2)}`
                ]);

                // Add grand total for patient summary
                const grandTotalAptEarnings = groupedData.reduce((sum, g) => sum + g.totals.appointmentEarnings, 0);
                patientSummaryData.push([
                    'GRAND TOTAL',
                    `Rs. ${toRupees(grandTotalAptEarnings).toFixed(2)}`
                ]);

                autoTable(doc, {
                    startY: yPosition,
                    head: [['Patient', 'Total Appointment Charge']],
                    body: patientSummaryData,
                    theme: 'striped',
                    headStyles: { fillColor: [41, 128, 185], fontSize: 10 },
                    bodyStyles: { fontSize: 9 },
                    didDrawPage: (data) => {
                        yPosition = data.cursor.y + 10;
                    }
                });

                yPosition = doc.lastAutoTable.finalY + 15;
            }

            // --- Summary 2: Clinic-wise total facility charge payable ---
            const clinicPayables = calculateClinicPayables(allTransactions);

            if (clinicPayables.length > 0) {
                if (yPosition > 250) {
                    doc.addPage();
                    yPosition = 20;
                }

                doc.setFontSize(14);
                doc.setFont(undefined, 'bold');
                doc.text('Summary: Clinic-wise Facility Charges Payable', 14, yPosition);
                yPosition += 6;

                const clinicSummaryData = clinicPayables.map(cp => [
                    cp.clinicName,
                    `Rs. ${toRupees(cp.amount).toFixed(2)}`
                ]);

                // Add grand total for clinic summary
                const grandTotalPayable = clinicPayables.reduce((sum, cp) => sum + cp.amount, 0);
                clinicSummaryData.push([
                    'GRAND TOTAL',
                    `Rs. ${toRupees(grandTotalPayable).toFixed(2)}`
                ]);

                autoTable(doc, {
                    startY: yPosition,
                    head: [['Clinic', 'Total Payable']],
                    body: clinicSummaryData,
                    theme: 'striped',
                    headStyles: { fillColor: [230, 126, 34], fontSize: 10 },
                    bodyStyles: { fontSize: 9 },
                    didDrawPage: (data) => {
                        yPosition = data.cursor.y + 10;
                    }
                });

                yPosition = doc.lastAutoTable.finalY + 15;
            }

            // Save the PDF — multiple strategies for mobile compatibility
            const filename = `earnings-report-${dateRangeLabel.replace(/\s+/g, '-').toLowerCase()}.pdf`;
            const blob = doc.output('blob');
            const file = new File([blob], filename, { type: 'application/pdf' });

            // Strategy 1: Use native share sheet on mobile (most reliable)
            if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
                try {
                    await navigator.share({ files: [file], title: 'Earnings Report' });
                } catch (shareErr) {
                    // User cancelled share — not an error
                    if (shareErr.name !== 'AbortError') {
                        console.warn('Share failed, falling back:', shareErr);
                    }
                }
            } else {
                // Strategy 2: Desktop / browsers without Web Share API
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(url), 5000);
            }
        } catch (error) {
            console.error('Failed to generate PDF:', error);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    // Helper to fetch all transactions for PDF export (bypasses pagination)
    const fetchAllTransactionsForExport = async () => {
        const now = new Date();
        const { rangeStart, rangeEnd } = getDateRange(dateRange, undefined, undefined, now);

        const [appointments, facility_charges, patients, users, clinics] = await Promise.all([
            db.appointments.toArray(),
            db.facility_charges.toArray(),
            db.patients.filter(p => !p.deleted).toArray(),
            db.users.toArray(),
            db.clinics.toArray()
        ]);

        const { appointments: filteredApps, facilityCharges: filteredComms } = filterFinancialRecords(
            appointments,
            facility_charges,
            doctorId,
            financialScope
        );

        return buildTransactions(
            filteredApps, filteredComms, patients, users, clinics,
            doctorId, rangeStart, rangeEnd, patientId || undefined
        );
    };

    const getDateRangeLabel = (range) => {
        const now = new Date();
        switch (range) {
            case 'current_month':
                return now.toLocaleString('default', { month: 'long', year: 'numeric' });
            case 'last_month': {
                const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
                return lastMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
            }
            case '2_months_ago': {
                const twoMonths = new Date(now.getFullYear(), now.getMonth() - 2);
                return twoMonths.toLocaleString('default', { month: 'long', year: 'numeric' });
            }
            case '3_months_ago': {
                const threeMonths = new Date(now.getFullYear(), now.getMonth() - 3);
                return threeMonths.toLocaleString('default', { month: 'long', year: 'numeric' });
            }
            default:
                return 'Custom Range';
        }
    };

    if (isLoading) {
        return (
            <div className="page-container">
                <Sidebar active="reports" onLogout={onLogout} />
                <div className="main-content">
                    <div style={{ textAlign: 'center', padding: '4rem', color: '#64748b' }}>
                        Loading...
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container" data-testid="reports-page">
            <Sidebar active="reports" onLogout={onLogout} />
            <div className="main-content">
                {/* Desktop Header */}
                <div className="header hidden md:flex">
                    <h1 data-testid="reports-title">Reports & Analytics</h1>
                    <button
                        className="btn btn-primary flex items-center gap-2"
                        onClick={handleExportPDF}
                        disabled={isExporting}
                        data-testid="export-pdf-button"
                    >
                        <FileDown size={18} />
                        {isExporting ? 'Generating…' : 'Export PDF'}
                    </button>
                </div>

                {/* Mobile Header */}
                <MobileHeader
                    title="Reports"
                    action={
                        <button
                            className="btn btn-sm btn-primary flex items-center gap-1"
                            onClick={handleExportPDF}
                            disabled={isExporting}
                        >
                            <FileDown size={16} />
                            {isExporting ? '…' : 'PDF'}
                        </button>
                    }
                />

                {/* Chart Card */}
                <div className="card mb-4" data-testid="earnings-chart-card">
                    <h2 className="text-lg font-semibold mb-4">Last 3 Months Earnings</h2>
                    <EarningsChart data={chartData} />
                </div>

                {/* Filters */}
                <div className="card mb-4">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        <h2 className="text-lg font-semibold">Transactions</h2>
                        <button
                            className="btn btn-outline flex items-center gap-2 md:hidden"
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <Filter size={16} />
                            Filters
                        </button>
                    </div>

                    <div className={`filter-row mt-4 ${showFilters ? '' : 'hidden md:flex'}`}>
                        <div className="filter-group">
                            <label htmlFor="date-range">Period</label>
                            <select
                                id="date-range"
                                value={dateRange}
                                onChange={(e) => { setDateRange(e.target.value); setPage(0); }}
                                className="form-select"
                                data-testid="date-range-filter"
                            >
                                <option value="current_month">This Month</option>
                                <option value="last_month">Last Month</option>
                                <option value="2_months_ago">2 Months Ago</option>
                                <option value="3_months_ago">3 Months Ago</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label htmlFor="patient-filter">Patient</label>
                            <select
                                id="patient-filter"
                                value={patientId}
                                onChange={(e) => { setPatientId(e.target.value); setPage(0); }}
                                className="form-select"
                                data-testid="patient-filter"
                            >
                                <option value="">All Patients</option>
                                {patients.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Transactions Table */}
                <div className="card" data-testid="transactions-table-card">
                    {transactions.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            No transactions found for the selected period.
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="data-table" data-testid="transactions-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Patient</th>
                                            <th className="text-right">Appt. Earnings</th>
                                            <th className="text-right">Facility Charge Earned</th>
                                            <th className="text-right">Facility Charge Payable</th>
                                            <th className="text-right">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.map(tx => (
                                            <tr key={tx.id} data-testid={`transaction-row-${tx.id}`}>
                                                <td>{new Date(tx.date).toLocaleDateString('en-IN')}</td>
                                                <td>{tx.patientName || '—'}</td>
                                                <td className="text-right">
                                                    {tx.appointmentEarnings > 0
                                                        ? <span className="text-emerald-600">{formatRupees(tx.appointmentEarnings)}</span>
                                                        : '—'
                                                    }
                                                </td>
                                                <td className="text-right">
                                                    {tx.facilityChargeEarned > 0
                                                        ? <div>
                                                            <span className="text-blue-600">{formatRupees(tx.facilityChargeEarned)}</span>
                                                            {tx.facilityChargeEarnedFromDoctor && (
                                                                <div className="text-xs text-slate-500">from {tx.facilityChargeEarnedFromDoctor}</div>
                                                            )}
                                                        </div>
                                                        : '—'
                                                    }
                                                </td>
                                                <td className="text-right">
                                                    {tx.facilityChargePayable > 0
                                                        ? <div>
                                                            <span className="text-orange-600">{formatRupees(tx.facilityChargePayable)}</span>
                                                            {tx.facilityChargePayableToDoctor && (
                                                                <div className="text-xs text-slate-500">to {tx.facilityChargePayableToDoctor}</div>
                                                            )}
                                                        </div>
                                                        : '—'
                                                    }
                                                </td>
                                                <td className="text-right font-medium">
                                                    <span style={{ color: tx.totalEarning >= 0 ? '#10b981' : '#ef4444' }}>
                                                        {formatRupees(tx.totalEarning)}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="pagination-container">
                                    <div className="text-sm text-slate-500">
                                        Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalCount)} of {totalCount}
                                    </div>
                                    <div className="pagination-controls">
                                        <button
                                            className="btn btn-outline btn-sm"
                                            onClick={() => setPage(Math.max(0, page - 1))}
                                            disabled={page === 0}
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <span className="px-3 text-sm">
                                            Page {page + 1} of {totalPages}
                                        </span>
                                        <button
                                            className="btn btn-outline btn-sm"
                                            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                                            disabled={page >= totalPages - 1}
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Styles */}
                <style>{`
                    .filter-row {
                        display: flex;
                        flex-wrap: wrap;
                        gap: 1rem;
                    }
                    
                    .filter-group {
                        display: flex;
                        flex-direction: column;
                        gap: 0.25rem;
                        min-width: 150px;
                    }
                    
                    .filter-group label {
                        font-size: 0.75rem;
                        font-weight: 500;
                        color: #64748b;
                    }
                    
                    .form-select {
                        padding: 0.5rem 0.75rem;
                        border: 1px solid #e2e8f0;
                        border-radius: 6px;
                        font-size: 0.875rem;
                        background: white;
                        cursor: pointer;
                    }
                    
                    .form-select:focus {
                        outline: none;
                        border-color: #0ea5e9;
                        box-shadow: 0 0 0 2px rgba(14, 165, 233, 0.2);
                    }
                    
                    .table-responsive {
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                    }
                    
                    .data-table {
                        width: 100%;
                        border-collapse: collapse;
                        font-size: 0.875rem;
                    }
                    
                    .data-table th,
                    .data-table td {
                        padding: 0.75rem;
                        border-bottom: 1px solid #e2e8f0;
                        text-align: left;
                    }
                    
                    .data-table th {
                        font-weight: 600;
                        color: #475569;
                        background: #f8fafc;
                        white-space: nowrap;
                    }
                    
                    .data-table tbody tr:hover {
                        background: #f8fafc;
                    }
                    
                    .text-right {
                        text-align: right;
                    }
                    
                    .pagination-container {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-top: 1rem;
                        padding-top: 1rem;
                        border-top: 1px solid #e2e8f0;
                        flex-wrap: wrap;
                        gap: 0.5rem;
                    }
                    
                    .pagination-controls {
                        display: flex;
                        align-items: center;
                        gap: 0.5rem;
                    }
                    
                    @media (max-width: 768px) {
                        .data-table {
                            font-size: 0.75rem;
                        }
                        
                        .data-table th,
                        .data-table td {
                            padding: 0.5rem;
                        }
                        
                        .filter-group {
                            width: 100%;
                        }
                    }
                `}</style>
            </div>
        </div>
    );
}

Reports.propTypes = {
    onLogout: PropTypes.func.isRequired
};
